// src/pages/community/EventsPage.jsx
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, ExternalLink, MapPin, Clock, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTenant } from '@/contexts/TenantContext';

/**
 * ============================================================================
 * EventsPage — Google Calendar Integration
 * ============================================================================
 *
 * Data source: Google Calendar API (public calendar)
 * Design:      Zelospace design system (raisin-black, mikado-yellow, swan)
 *
 * ─── PER-PARTNER CALENDAR ────────────────────────────────────────────────
 * Calendar ID and API key are read from the Tenant branding JSON field
 * in Django admin. Set branding.calendar_id and branding.calendar_api_key
 * for the partner. Falls back to VITE_GOOGLE_CALENDAR_ID and
 * VITE_GOOGLE_CALENDAR_API_KEY env vars if not set (Zelospace default).
 * ─────────────────────────────────────────────────────────────────────────
 *
 * To add/edit events:
 *   1. Go to calendar.google.com
 *   2. Open the relevant calendar
 *   3. Create event — add Eventbrite URL in the Description field like:
 *      REGISTER: https://www.eventbrite.com/e/your-event
 *   4. Add category in title brackets: [Workshop], [Meetup], [Competition]
 *
 * Example event title: "[Workshop] Photography Masterclass"
 * ============================================================================
 */

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const extractCategory = (title = '') => {
  const match = title.match(/^\[([^\]]+)\]/);
  return match ? match[1] : 'Event';
};

const cleanTitle = (title = '') => {
  return title.replace(/^\[[^\]]+\]\s*/, '');
};

const extractEventbriteUrl = (description = '') => {
  const match = description.match(/REGISTER:\s*(https?:\/\/[^\s<]+)/i);
  return match ? match[1] : null;
};

const CATEGORY_COLORS = {
  'Workshop':    'bg-mikado-yellow/10 text-mikado-yellow border-mikado-yellow/20',
  'Meetup':      'bg-green-500/10 text-green-400 border-green-500/20',
  'Competition': 'bg-imperial-red/10 text-imperial-red border-imperial-red/20',
  'Event':       'bg-swan/10 text-swan border-swan/20',
};

const getCategoryStyle = (category) =>
  CATEGORY_COLORS[category] || CATEGORY_COLORS['Event'];

const formatDisplayDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatTime = (start, end) => {
  if (!start?.dateTime) return 'All Day';
  const startTime = new Date(start.dateTime).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const endTime = end?.dateTime
    ? new Date(end.dateTime).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;
  return endTime ? `${startTime} – ${endTime}` : startTime;
};

const getEventDate = (event) => {
  const dateStr = event.start?.dateTime || event.start?.date;
  return dateStr ? new Date(dateStr) : null;
};

const EventsPage = () => {
  // ✅ useTenant called inside component — correct
  const { branding } = useTenant();
  const CALENDAR_ID = branding?.calendar_id || import.meta.env.VITE_GOOGLE_CALENDAR_ID;
  const API_KEY = branding?.calendar_api_key || import.meta.env.VITE_GOOGLE_CALENDAR_API_KEY;

  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // ✅ CALENDAR_ID and API_KEY in dependency array so effect re-runs if branding loads
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      setError(null);

      try {
        const timeMin = new Date(currentYear, currentMonth - 1, 1).toISOString();
        const timeMax = new Date(currentYear, currentMonth + 2, 0).toISOString();

        const url = new URL(
          `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(CALENDAR_ID)}/events`
        );
        url.searchParams.set('key', API_KEY);
        url.searchParams.set('timeMin', timeMin);
        url.searchParams.set('timeMax', timeMax);
        url.searchParams.set('singleEvents', 'true');
        url.searchParams.set('orderBy', 'startTime');
        url.searchParams.set('maxResults', '50');

        const res = await fetch(url.toString());

        if (!res.ok) {
          throw new Error(`Failed to fetch events: ${res.status}`);
        }

        const data = await res.json();
        setEvents(data.items || []);
      } catch (err) {
        console.error('[EventsPage] Failed to fetch events:', err);
        setError('Failed to load events. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (CALENDAR_ID && API_KEY) {
      fetchEvents();
    } else {
      setError('Calendar not configured.');
      setLoading(false);
    }
  }, [currentMonth, currentYear, CALENDAR_ID, API_KEY]);

  const getEventsForDay = (day) => {
    return events.filter(event => {
      const eventDate = getEventDate(event);
      return (
        eventDate &&
        eventDate.getDate() === day &&
        eventDate.getMonth() === currentMonth &&
        eventDate.getFullYear() === currentYear
      );
    });
  };

  const getMonthEvents = () => {
    return events.filter(event => {
      const eventDate = getEventDate(event);
      return (
        eventDate &&
        eventDate.getMonth() === currentMonth &&
        eventDate.getFullYear() === currentYear
      );
    });
  };

  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const today = new Date();

  const previousMonth = () => {
    setSelectedDay(null);
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const nextMonth = () => {
    setSelectedDay(null);
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const monthEvents = getMonthEvents();
  const selectedDayEvents = selectedDay ? getEventsForDay(selectedDay) : [];

  return (
    <div className="min-h-screen w-full bg-stone-brown px-4 py-20">
      <div className="max-w-5xl mx-auto">

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white font-poppins mb-2">
            Events
          </h1>
          <p className="text-swan/60 text-sm">
            Workshops, meetups and competitions - click a day to see what's on
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── Left: Calendar Grid ── */}
          <div className="lg:col-span-2">
            <div className="bg-raisin-black rounded-xl p-6">

              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white font-poppins">
                  {MONTH_NAMES[currentMonth]} {currentYear}
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={previousMonth}
                    className="p-2 rounded-lg text-swan/60 hover:text-white hover:bg-stone-brown/40 transition-all"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={nextMonth}
                    className="p-2 rounded-lg text-swan/60 hover:text-white hover:bg-stone-brown/40 transition-all"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 mb-2">
                {DAY_NAMES.map(day => (
                  <div key={day} className="text-center text-xs font-semibold text-swan/40 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="animate-spin text-mikado-yellow" size={28} />
                </div>
              ) : (
                <div className="grid grid-cols-7 gap-1">
                  {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                    <div key={`empty-${i}`} className="aspect-square" />
                  ))}

                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const dayEvents = getEventsForDay(day);
                    const hasEvents = dayEvents.length > 0;
                    const isToday =
                      day === today.getDate() &&
                      currentMonth === today.getMonth() &&
                      currentYear === today.getFullYear();
                    const isSelected = selectedDay === day;

                    return (
                      <button
                        key={day}
                        onClick={() => setSelectedDay(isSelected ? null : day)}
                        className={`
                          aspect-square rounded-lg flex flex-col items-center justify-center
                          text-sm font-medium transition-all relative
                          ${isSelected
                            ? 'bg-mikado-yellow text-stone-brown font-bold'
                            : isToday
                            ? 'bg-imperial-red/20 text-imperial-red font-bold'
                            : hasEvents
                            ? 'bg-stone-brown/40 text-white hover:bg-stone-brown/60'
                            : 'text-swan/40 hover:bg-stone-brown/30 hover:text-swan/70'
                          }
                        `}
                      >
                        {day}
                        {hasEvents && !isSelected && (
                          <span className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                            {dayEvents.slice(0, 3).map((_, idx) => (
                              <span
                                key={idx}
                                className="w-1 h-1 rounded-full bg-mikado-yellow"
                              />
                            ))}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-pebble-brown/20">
                <div className="flex items-center gap-1.5 text-xs text-swan/50">
                  <span className="w-2 h-2 rounded-full bg-mikado-yellow" />
                  Has events
                </div>
                <div className="flex items-center gap-1.5 text-xs text-swan/50">
                  <span className="w-2 h-2 rounded-full bg-imperial-red/60" />
                  Today
                </div>
              </div>
            </div>
          </div>

          {/* ── Right: Event List ── */}
          <div className="lg:col-span-1">
            <div className="bg-raisin-black rounded-xl p-5 h-full">

              <h3 className="text-sm font-semibold text-swan/50 uppercase tracking-wider mb-4">
                {selectedDay
                  ? `${MONTH_NAMES[currentMonth]} ${selectedDay}`
                  : `${MONTH_NAMES[currentMonth]} Events`}
              </h3>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-mikado-yellow" size={20} />
                </div>
              ) : error ? (
                <div className="flex items-start gap-2 p-3 bg-red-500/10 rounded-lg">
                  <AlertCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              ) : (
                <div className="space-y-3 overflow-y-auto max-h-[500px] pr-1">
                  {(selectedDay ? selectedDayEvents : monthEvents).length === 0 ? (
                    <div className="text-center py-10">
                      <Calendar size={32} className="text-swan/20 mx-auto mb-3" />
                      <p className="text-swan/40 text-sm">
                        {selectedDay
                          ? 'No events this day'
                          : `No events in ${MONTH_NAMES[currentMonth]}`}
                      </p>
                    </div>
                  ) : (
                    (selectedDay ? selectedDayEvents : monthEvents).map(event => {
                      const category = extractCategory(event.summary);
                      const title = cleanTitle(event.summary);
                      const eventbriteUrl = extractEventbriteUrl(event.description || '');
                      const location = event.location;
                      const time = formatTime(event.start, event.end);
                      const date = formatDisplayDate(
                        event.start?.dateTime || event.start?.date
                      );

                      return (
                        <div
                          key={event.id}
                          className="bg-stone-brown/30 rounded-lg p-4 border border-pebble-brown/20 hover:border-mikado-yellow/20 transition-all"
                        >
                          <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded border mb-2 ${getCategoryStyle(category)}`}>
                            {category}
                          </span>

                          <h4 className="text-white font-semibold text-sm mb-2 leading-snug">
                            {title}
                          </h4>

                          {!selectedDay && (
                            <div className="flex items-center gap-1.5 text-swan/60 text-xs mb-1">
                              <Calendar size={12} className="text-mikado-yellow" />
                              <span>{date}</span>
                            </div>
                          )}

                          <div className="flex items-center gap-1.5 text-swan/60 text-xs mb-1">
                            <Clock size={12} className="text-mikado-yellow" />
                            <span>{time}</span>
                          </div>

                          {location && (
                            <div className="flex items-center gap-1.5 text-swan/60 text-xs mb-3">
                              <MapPin size={12} className="text-mikado-yellow" />
                              <span>{location}</span>
                            </div>
                          )}

                          {eventbriteUrl && (
                            <a
                              href={eventbriteUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <Button
                                variant="primary"
                                size="sm"
                                className="w-full flex items-center justify-center gap-1.5 text-xs"
                              >
                                Register
                                <ExternalLink size={12} />
                              </Button>
                            </a>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Host an Event CTA */}
        <div className="mt-6 bg-raisin-black rounded-xl p-6 flex items-center justify-between border border-pebble-brown/20">
          <div>
            <h3 className="text-white font-bold font-poppins mb-1">
              Want to host an event?
            </h3>
            <p className="text-swan/60 text-sm">
              Get in touch to feature your event on our calendar.
            </p>
          </div>
          <a
            href="https://www.zelospace.com/partner"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="primary" size="sm">
              Contact Us
            </Button>
          </a>
        </div>

      </div>
    </div>
  );
};

export default EventsPage;